import { AlertTriangle, Check, Copy, Loader2 } from "lucide-react";
import * as React from "react";
import { BrandButton } from "@/components/brand/brand-button";
import { BrandInput, BrandInputGroup } from "@/components/brand/brand-input";
import { useWebHaptics } from "@/lib/haptics";
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "./alert-dialog";

interface ConfirmDeleteDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	children?: React.ReactNode;
}

interface ConfirmDeleteDialogContentProps {
	children: React.ReactNode;
}

interface ConfirmDeleteDialogHeaderProps {
	title: string;
	description: string;
	icon?: React.ReactNode;
}

interface ConfirmDeleteDialogConfirmationProps {
	value: string;
	onChange: (value: string) => void;
	confirmText: string;
	label?: string;
	placeholder?: string;
}

interface ConfirmDeleteDialogFooterProps {
	onCancel: () => void;
	onConfirm: () => void;
	confirmDisabled?: boolean;
	isLoading?: boolean;
	cancelLabel?: string;
	confirmLabel?: string;
}

const ConfirmDeleteDialog = ({
	open,
	onOpenChange,
	children,
}: ConfirmDeleteDialogProps) => (
	<AlertDialog open={open} onOpenChange={onOpenChange}>
		{children}
	</AlertDialog>
);

const ConfirmDeleteDialogTrigger = AlertDialogTrigger;

const ConfirmDeleteDialogContent = ({
	children,
}: ConfirmDeleteDialogContentProps) => (
	<AlertDialogContent>{children}</AlertDialogContent>
);

const ConfirmDeleteDialogHeader = ({
	title,
	description,
	icon,
}: ConfirmDeleteDialogHeaderProps) => (
	<AlertDialogHeader>
		<div className="flex items-start gap-3">
			<div className="flex shrink-0 items-center justify-center size-9 border border-[color:var(--ticker-down)]/40 bg-destructive/10 text-[color:var(--ticker-down)]">
				{icon || <AlertTriangle className="size-4" />}
			</div>
			<div className="flex-1">
				<AlertDialogTitle className="font-mono font-normal text-base uppercase tracking-[0.08em] text-foreground">
					{title}
				</AlertDialogTitle>
				<AlertDialogDescription className="mt-1 text-left text-base">
					{description}
				</AlertDialogDescription>
			</div>
		</div>
	</AlertDialogHeader>
);

const ConfirmDeleteDialogConfirmation = ({
	value,
	onChange,
	confirmText,
	label,
	placeholder,
}: ConfirmDeleteDialogConfirmationProps) => {
	const [copied, setCopied] = React.useState(false);

	const handleCopy = () => {
		navigator.clipboard.writeText(confirmText);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	const displayLabel = label || (
		<>
			Type{" "}
			<span className="inline-flex items-center gap-1 normal-case">
				<span className="font-mono text-foreground">"{confirmText}"</span>
				<button
					type="button"
					onClick={handleCopy}
					className="relative p-0.5 text-muted-foreground transition-[color,transform] hover:text-brand active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand before:absolute before:-inset-2.5 before:content-['']"
					aria-label={copied ? "Copied" : `Copy "${confirmText}" to clipboard`}
				>
					{copied ? (
						<Check className="size-3 text-brand" />
					) : (
						<Copy className="size-3" />
					)}
				</button>
			</span>{" "}
			to confirm
		</>
	);

	return (
		<BrandInputGroup>
			<label className="block font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
				{displayLabel}
			</label>
			<BrandInput
				// biome-ignore lint/a11y/noAutofocus: destructive confirmation dialog
				autoFocus
				value={value}
				onChange={(e) => onChange(e.target.value)}
				placeholder={placeholder || `Type ${confirmText}`}
				variant="error"
			/>
		</BrandInputGroup>
	);
};

const ConfirmDeleteDialogFooter = ({
	onCancel,
	onConfirm,
	confirmDisabled = false,
	isLoading = false,
	cancelLabel,
	confirmLabel,
}: ConfirmDeleteDialogFooterProps) => {
	const { trigger } = useWebHaptics();

	return (
		<AlertDialogFooter>
			<BrandButton
				type="button"
				variant="secondary"
				onClick={onCancel}
				disabled={isLoading}
			>
				{cancelLabel ?? "Cancel"}
			</BrandButton>
			<BrandButton
				type="button"
				variant="destructive"
				onClick={() => {
					trigger("success");
					onConfirm();
				}}
				disabled={confirmDisabled || isLoading}
			>
				{isLoading ? (
					<>
						<Loader2 className="size-4 animate-spin" />
						Deleting…
					</>
				) : (
					(confirmLabel ?? "Delete")
				)}
			</BrandButton>
		</AlertDialogFooter>
	);
};

export {
	ConfirmDeleteDialog,
	ConfirmDeleteDialogTrigger,
	ConfirmDeleteDialogContent,
	ConfirmDeleteDialogHeader,
	ConfirmDeleteDialogConfirmation,
	ConfirmDeleteDialogFooter,
};
